import { useEffect, useRef } from "react"

const PANEL_SELECTOR = ".AugmentationPanel"
const FOCUS_CHECK_DELAYS_MS = [0, 100, 300]

/**
 * Returns keyboard focus to where it was before the panel opened.
 *
 * The panel is opened by routes, not by a trigger element it knows about, so
 * this watches focus instead: it remembers the last element focused outside
 * the panel, and focuses it again when the panel closes.
 *
 * While the panel is open it also keeps focus inside as the panel moves
 * between levels.
 *
 * It only acts when the panel was closed from the keyboard. After a mouse
 * click there is no focus to lose, and refocusing would leave hover-revealed
 * controls showing.
 */
function useAugmentationPanelFocusReturn(
  open: boolean | undefined,
  /** The panel's current route. Focus is kept inside the panel as it changes. */
  path?: string | null
) {
  const lastFocusedOutside = useRef<HTMLElement | null>(null)
  const lastInputWasKeyboard = useRef(false)
  const wasOpen = useRef(false)

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (target.closest(PANEL_SELECTOR)) return
      lastFocusedOutside.current = target
    }
    const onKeyDown = () => {
      lastInputWasKeyboard.current = true
    }
    const onPointerDown = () => {
      lastInputWasKeyboard.current = false
    }
    document.addEventListener("focusin", onFocusIn, true)
    document.addEventListener("keydown", onKeyDown, true)
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => {
      document.removeEventListener("focusin", onFocusIn, true)
      document.removeEventListener("keydown", onKeyDown, true)
      document.removeEventListener("pointerdown", onPointerDown, true)
    }
  }, [])

  useEffect(() => {
    const closed = wasOpen.current && !open
    wasOpen.current = !!open
    if (!closed || !lastInputWasKeyboard.current) return
    const opener = lastFocusedOutside.current
    // The opener may be gone, e.g. a Placeholder that was just filled.
    if (!opener?.isConnected) return
    // Wait for the panel to finish unmounting its own focus handling.
    const frame = requestAnimationFrame(() =>
      opener.focus({ preventScroll: true })
    )
    return () => cancelAnimationFrame(frame)
  }, [open])

  // Moving between levels of the panel unmounts whatever had focus, which
  // would drop focus to the page. Keep it in the panel.
  useEffect(() => {
    if (!open || !lastInputWasKeyboard.current) return
    const keepFocusInPanel = () => {
      const active = document.activeElement
      const focusWasLost = !active || active === document.body
      if (!focusWasLost) return
      document
        .querySelector<HTMLElement>(PANEL_SELECTOR)
        ?.querySelector<HTMLElement>(
          "a[href], button:not([disabled]), input, select, textarea"
        )
        ?.focus({ preventScroll: true })
    }
    // The new level renders, and the old one unmounts, over a few frames.
    const timers = FOCUS_CHECK_DELAYS_MS.map((delay) =>
      window.setTimeout(keepFocusInPanel, delay)
    )
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [open, path])
}

export default useAugmentationPanelFocusReturn
